// Window Care — subcontract pass-through pricing (mirror of
// backend/services/window_care.py). The sub's rate and the customer price are
// both entered values; the 15-25% deep-clean adder raises BOTH, and a per-visit
// job minimum tops the visit up when the panes don't reach it.

export const WC_SCOPE_NAME = "Window Care";
export const WC_ADDER_NAME = "Initial deep clean / large-project logistics";
export const WC_MINIMUM_NAME = "Job minimum per visit";

export const WC_DEFAULTS = {
  enabled: true,
  visits: 1,
  adder_enabled: true,
  adder_pct: 25,
  adder_min_pct: 15,
  adder_max_pct: 25,
  min_price: 250,
  min_sub: 137.5,
  // Interior + exterior: the interior add then applies to every opening, so
  // its quantity is derived from the standard + large counts.
  both_sides: false,
  rates: [
    { key: "standard_ext", part_number: "WC-PANE-STD-EXT", name: "Standard pane — exterior", qty: 0, sub_cost: 6, price: 11 },
    { key: "large", part_number: "WC-PANE-LARGE", name: "Large pane (over ~15 sq ft)", qty: 0, sub_cost: 7.5, price: 14 },
    { key: "second_story", part_number: "WC-ADD-2ND-STORY", name: "2nd story add — per pane", qty: 0, sub_cost: 2.75, price: 5 },
    { key: "interior", part_number: "WC-ADD-INTERIOR", name: "Interior add — per pane", qty: 0, sub_cost: 4.5, price: 8 },
  ],
};

const n = (v) => {
  const f = parseFloat(v);
  return Number.isFinite(f) ? f : 0;
};
const c2 = (v) => Math.round(n(v) * 100) / 100;

export const isWindowCareScope = (scope) =>
  !!scope && (scope.window_care?.enabled === true ||
    (scope.name || "").trim().toLowerCase() === WC_SCOPE_NAME.toLowerCase());

export const WC_OPENING_KEYS = ["standard_ext", "large"];
export const WC_COUNT_KEYS = ["standard_ext", "large", "second_story", "interior"];

export const wcConfig = (scope) => {
  const cfg = {
    ...WC_DEFAULTS,
    ...(scope?.window_care || {}),
    rates: (scope?.window_care?.rates?.length ? scope.window_care.rates : WC_DEFAULTS.rates).map(r => ({ ...r })),
  };
  if (cfg.both_sides) {
    const openings = cfg.rates
      .filter(r => WC_OPENING_KEYS.includes(r.key))
      .reduce((s, r) => s + (parseFloat(r.qty) || 0), 0);
    cfg.rates = cfg.rates.map(r => (r.key === "interior" ? { ...r, qty: openings } : r));
  }
  return cfg;
};

// Pane counts ⇄ the rate rows. Counts are what gets saved per site.
export const wcCountsFromConfig = (cfg) => {
  const out = { both_sides: !!cfg.both_sides };
  WC_COUNT_KEYS.forEach(k => {
    out[k] = Math.round(parseFloat(cfg.rates.find(r => r.key === k)?.qty) || 0);
  });
  return out;
};

export const wcApplyCounts = (cfg, counts) => ({
  ...cfg,
  both_sides: !!counts.both_sides,
  rates: cfg.rates.map(r => (WC_COUNT_KEYS.includes(r.key)
    ? { ...r, qty: Math.max(0, Math.round(parseFloat(counts[r.key]) || 0)) }
    : r)),
});

export const wcCountsAreEmpty = (counts) =>
  WC_COUNT_KEYS.every(k => !(parseFloat(counts?.[k]) > 0));

// Build-up for ONE visit plus the totals across visits.
export const wcQuote = (cfg) => {
  const visits = Math.max(1, parseInt(cfg.visits) || 1);
  const pct = cfg.adder_enabled ? n(cfg.adder_pct) : 0;
  const rows = (cfg.rates || [])
    .filter(r => n(r.qty) > 0)
    .map(r => ({
      ...r,
      qty: n(r.qty),
      ext_price: c2(n(r.qty) * n(r.price)),
      ext_sub: c2(n(r.qty) * n(r.sub_cost)),
    }));
  const basePrice = c2(rows.reduce((s, r) => s + n(r.qty) * n(r.price), 0));
  const baseSub = c2(rows.reduce((s, r) => s + n(r.qty) * n(r.sub_cost), 0));
  const adderPrice = c2(basePrice * pct / 100);
  const adderSub = c2(baseSub * pct / 100);
  const visitPrice = c2(basePrice + adderPrice);
  const visitSub = c2(baseSub + adderSub);
  const minPrice = n(cfg.min_price);
  const minSub = n(cfg.min_sub);
  const topupPrice = visitPrice > 0 ? c2(Math.max(0, minPrice - visitPrice)) : 0;
  const topupSub = visitPrice > 0 ? c2(Math.max(0, minSub - visitSub)) : 0;
  const totalPrice = c2((visitPrice + topupPrice) * visits);
  const totalSub = c2((visitSub + topupSub) * visits);
  return {
    rows, visits, adderPct: pct, basePrice, baseSub, adderPrice, adderSub,
    visitPrice, visitSub, minPrice, minSub, topupPrice, topupSub,
    minimumApplied: topupPrice > 0,
    totalPrice, totalSub,
    profit: c2(totalPrice - totalSub),
    marginPct: totalPrice > 0 ? Math.round(((totalPrice - totalSub) / totalPrice) * 1000) / 10 : 0,
  };
};

const wcLine = (key, name, qty, price, subCost, description, visits = 1) => ({
  id: `wc-${key}`,
  wc_key: key,
  wc_visits: visits,
  name,
  description: description || "",
  part_number: "",
  qty,
  pricing_mode: "subcontract",
  flat_rate: c2(price),
  subcontract_cost: c2(subCost),
  cost_type: "3",
  material_cost: 0,
  material_margin: 0,
  labor_cost: 0,
  labor_margin: 0,
  labor_hours: 0,
  freight: 0,
  freight_type: "unit",
  freight_margin: 0,
  location: "",
  manufacturer: "",
});

// The scope's line items, rebuilt from the panel state. Pane quantities are
// PER VISIT, so a 2-visit quote doubles each line's qty; the adder and the
// minimum top-up are one line each priced per visit.
export const wcLineItems = (cfg) => {
  const q = wcQuote(cfg);
  const out = [];
  for (const r of q.rows) {
    out.push(wcLine(r.key, r.name, Math.round(r.qty * q.visits), r.price, r.sub_cost,
      q.visits > 1 ? `${r.qty} per visit × ${q.visits} visits` : "", q.visits));
  }
  if (out.length && q.adderPrice > 0) {
    out.push(wcLine("adder", `${WC_ADDER_NAME} (${q.adderPct}%)`, q.visits, q.adderPrice, q.adderSub, "", q.visits));
  }
  if (out.length && q.topupPrice > 0) {
    out.push(wcLine("minimum", WC_MINIMUM_NAME, q.visits, q.topupPrice, q.topupSub,
      `Tops the visit up to the ${WC_MINIMUM_NAME.toLowerCase()}`, q.visits));
  }
  return out;
};

export const isWcLine = (item) => !!item?.wc_key;
